import { IncomeSource } from '../.../../../../generated/graphql';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '../Checkbox';
import { useIncomeSources, useUpdateIncomeSourceService } from '../../../IncomeSource';
import { recurringMonetaryAmountDataPointValue } from '../../../DataPoint';
import { formatUTCDate } from '../../../util';
import { AmountDisplay } from '../AmountDisplay';

interface ModalProps {
  householdID: string;
}

export const AdjustedAnnualSpendingToggleModal: React.FC<ModalProps> = ({ householdID }) => {
  const { incomeSources = [] } = useIncomeSources({ householdID });
  const futureIncomeSources = incomeSources.filter((source) => source.isFutureGuaranteedIncome);

  return (
    <div className="w-96 h-fit max-w-[calc(100vw_-_32px)] p-2 bg-[#2b2e31] rounded-2xl shadow flex-col justify-start items-center inline-flex">
      <div className="items-center justify-between mt-2">
        <AllFutureIncomeModalLineItem items={futureIncomeSources} />
        <div className="m-2">
          <div className="h-px bg-darkMode-gray-medium opacity-50" />
        </div>
        {futureIncomeSources &&
          futureIncomeSources.map((item) => <FutureIncomeModalLineItem item={item} key={item.id} />)}
      </div>
    </div>
  );
};

export const FutureIncomeModalLineItem = ({ item }: { item: IncomeSource }) => {
  const { updateIncomeSourceService } = useUpdateIncomeSourceService();

  return (
    <div className="h-14 p-2 bg-darkMode-gray justify-between items-center inline-flex w-full">
      <div className="flex-col justify-start items-start inline-flex">
        <div className="text-white text-base">{item.name}</div>
        <div className="opacity-70 text-xs ">
          {formatUTCDate(item.amountDataPointGroup?.latestDataPoint?.dateTime, 'M/d/yyyy')}
        </div>
      </div>
      <div className="justify-start items-center flex">
        <AmountDisplay amount={recurringMonetaryAmountDataPointValue(item.amountDataPointGroup?.latestDataPoint)} />
        <Checkbox
          className="ml-2"
          type="square"
          iconTheme={'primary'}
          defaultChecked={!item.omitFromScorecard}
          onChange={() => {
            updateIncomeSourceService.mutate({
              updateIncomeSourceInput: {
                changes: { omitFromScorecard: !item.omitFromScorecard },
                householdID: item.householdID,
                changeToken: item.changeToken,
                id: item.id,
              },
            });
          }}
        ></Checkbox>
      </div>
    </div>
  );
};

export const AllFutureIncomeModalLineItem = ({ items }: { items: IncomeSource[] }) => {
  const { t: tUI } = useTranslation('UI');
  return (
    <div className="h-10 p-2  justify-between items-center inline-flex w-full bg-darkMode-gray">
      <div className="flex-row items-start bg-darkMode-gray">
        <div className="text-white text-base">{tUI('all-guaranteed-income')}</div>
      </div>
      <div className="items-end flex-row">
        <AllFutureIncomeCheckbox items={items} />
      </div>
    </div>
  );
};

export const toggleAllFutureIncome = ({
  items,
  updateIncomeSourceService,
}: {
  items: IncomeSource[];
  updateIncomeSourceService: any;
}) => {
  const allChecked = items.every((item) => !item.omitFromScorecard);
  items.forEach((item) => {
    updateIncomeSourceService.mutate({
      updateIncomeSourceInput: {
        changes: { omitFromScorecard: allChecked },
        householdID: item.householdID,
        changeToken: item.changeToken,
        id: item.id,
      },
    });
  });
};

export const AllFutureIncomeCheckbox = ({ items }: { items: IncomeSource[] }) => {
  const { updateIncomeSourceService } = useUpdateIncomeSourceService();

  const allChecked = items.every((item) => !item.omitFromScorecard);
  const allUnchecked = items.every((item) => item.omitFromScorecard);
  return (
    <Checkbox
      className="ml-2"
      type="square"
      iconTheme={'primary'}
      defaultChecked={allChecked}
      partiallyChecked={!allChecked && !allUnchecked}
      onChange={() => {
        toggleAllFutureIncome({ items, updateIncomeSourceService });
      }}
    ></Checkbox>
  );
};
